// Admin Users page (ionic-tier only).
//
// Lists every user in the platform's identity directory with a
// case-insensitive substring search over email / entra_upn / display_name.
// Only users with at least one ionic-tier role reach this page — gated at
// two layers: the API (requireIonic) and the nav link in App.tsx.
//
// The debounce lives here — not in the hook — so the query key stays stable
// for 300ms of typing.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useMe } from '../me/use-me.js';
import { useAdminUsers, type AdminUserItem } from './use-admin-users.js';

export function AdminUsersPage(): JSX.Element {
  const { me, loading: meLoading } = useMe();
  const hasIonic = (me?.ionicRoles ?? []).length > 0;

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useAdminUsers({
    search: debouncedSearch,
  });

  if (meLoading) return <p className="text-slate-500">Loading…</p>;

  if (!hasIonic) {
    return (
      <div
        role="alert"
        className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
      >
        <p className="font-medium">Not authorized.</p>
        <p>This page is restricted to Ionic-tier roles.</p>
      </div>
    );
  }

  const rows = (data?.pages ?? []).flatMap((p) => p.items);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Users (Admin)</h1>
          <p className="mt-1 text-sm text-slate-600">
            Every user who has signed into the portal at least once.
          </p>
        </div>
        <Link to="/" className="text-sm text-brand-600 hover:text-brand-700">
          ← Back
        </Link>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <label
          className="mb-1 block text-xs font-medium text-slate-600"
          htmlFor="admin-user-search"
        >
          Search
        </label>
        <input
          id="admin-user-search"
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="name, email, or UPN"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </section>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error.message}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Display name</th>
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Entra UPN</th>
              <th className="px-4 py-2 font-medium">Active</th>
              <th className="px-4 py-2 font-medium">Created</th>
              <th className="px-4 py-2 font-medium">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                  No users match the current filter.
                </td>
              </tr>
            ) : (
              rows.map((u) => <AdminUserRow key={u.id} user={u} />)
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {rows.length} user{rows.length === 1 ? '' : 's'} loaded
        </p>
        {hasNextPage && (
          <button
            type="button"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="rounded-md border border-slate-300 px-3 py-1 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  );
}

function AdminUserRow(props: { user: AdminUserItem }): JSX.Element {
  const u = props.user;
  return (
    <tr>
      <td className="px-4 py-3 font-medium text-slate-900">{u.displayName}</td>
      <td className="px-4 py-3 text-slate-700">{u.email}</td>
      <td className="px-4 py-3 font-mono text-xs text-slate-600">{u.entraUpn}</td>
      <td className="px-4 py-3">
        <span
          className={clsx(
            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
            u.active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600',
          )}
        >
          {u.active ? 'active' : 'inactive'}
        </span>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-500">
        {new Date(u.createdAt).toLocaleString()}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-500">
        {new Date(u.updatedAt).toLocaleString()}
      </td>
    </tr>
  );
}
